import { FileText, HardHat, Package, UserRound } from 'lucide-react';
import type { WorkOrderDetailViewModel } from '../types/workOrderDetail.types';
import type { ElementType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface DetailSidebarProps {
  data: WorkOrderDetailViewModel;
}

export function DetailSidebar({ data }: DetailSidebarProps) {
  return (
    <aside className="space-y-4">
      <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3">
        <h2 className="text-sm font-normal text-slate-950">Trạng thái</h2>
        <SelectLike label="Trạng thái" value={data.statusLabel} tone="bg-blue-50 text-blue-600" />
        <SelectLike label="Ưu tiên" value={data.priorityLabel} tone="bg-rose-50 text-rose-600" />
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm font-normal">
            <span className="text-slate-500">Tiến độ</span>
            <span className="text-slate-900">{data.progress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-md bg-slate-100">
            <div className="h-full rounded-md bg-emerald-700" style={{ width: `${data.progress}%` }} />
          </div>
        </div>
      </section>

      <ListBlock 
        title={`Công việc con (${data.checklists.length})`} 
        icon={FileText} 
        empty="Chưa có công việc con"
        action={<Link to={`/work-orders/detail/${data.id}/sub-tasks`} className="shrink-0 sm:text-sm text-xs text-emerald-600 hover:text-emerald-800">Quản lý chi tiết</Link>}
      >
        {data.checklists.slice(0, 5).map((item, index) => (
          <CompactItem
            key={item.id || index}
            title={item.title || item.name || item.content || `Công việc con ${index + 1}`}
            meta={item.is_done || item.completed ? 'Hoàn thành' : 'Chưa hoàn thành'}
          />
        ))}
      </ListBlock>

      <ListBlock title={`Vật tư (${data.materials.length})`} icon={Package} empty="Chưa có vật tư">
        {data.materials.slice(0, 5).map((item, index) => (
          <CompactItem
            key={item.id || index}
            title={item.material_name || item.name || item.item_name || `Vật tư ${index + 1}`}
            meta={`${item.quantity ?? item.planned_quantity ?? 0} ${item.unit || ''}`.trim()}
          />
        ))}
      </ListBlock>

      <ListBlock title={`Thiết bị (${data.equipment.length})`} icon={HardHat} empty="Chưa có thiết bị">
        {data.equipment.slice(0, 5).map((item, index) => (
          <CompactItem
            key={item.id || index}
            title={item.equipment_name || item.name || item.code || `Thiết bị ${index + 1}`}
            meta={item.status_label || item.status_code || 'Đang theo dõi'}
          />
        ))}
      </ListBlock>

      <ListBlock title={`Thành viên (${data.members.length})`} icon={UserRound} empty="Chưa có thành viên">
        {data.members.slice(0, 5).map((item, index) => (
          <CompactItem
            key={item.id || index}
            title={item.user?.full_name || item.full_name || item.name || `Thành viên ${index + 1}`}
            meta={item.role_label || item.role_code || 'Thành viên'}
          />
        ))}
      </ListBlock>
    </aside>
  );
}

function SelectLike({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="space-y-2">
      <p className="sm:text-sm text-xs text-slate-500">{label}</p>
      <div className={`flex h-10 items-center justify-between rounded-md border border-slate-100 px-3 text-sm font-normal ${tone}`}>
        <span>{value || '---'}</span>
        <i className="ri-arrow-down-s-line" />
      </div>
    </div>
  );
}

function ListBlock({
  title,
  icon: Icon,
  empty,
  action,
  children,
}: {
  title: string;
  icon: ElementType;
  empty: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-emerald-600" />
          <h2 className="truncate text-sm font-normal text-slate-950">{title}</h2>
        </div>
        {action || (
          <button type="button" className="shrink-0 sm:text-sm text-xs text-emerald-600 hover:text-emerald-800">
            Xem tất cả
          </button>
        )}
      </div>
      <div className="space-y-2">
        {hasChildren ? children : <p className="rounded-md bg-slate-50 p-3 text-sm font-normal text-slate-500">{empty}</p>}
      </div>
    </section>
  );
}

function CompactItem({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <p className="line-clamp-1 text-sm font-normal text-slate-800">{title}</p>
      <p className="mt-1 sm:text-sm text-xs text-slate-500">{meta || '---'}</p>
    </div>
  );
}
